import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { DeviceVariables } from 'src/constants/deviceConstants';
import { addModal } from 'src/features/globalSlice';
import { routes } from 'src/routes';
import { getErrorsTable } from 'src/store/actions/productActions';
import { DeviceError } from 'src/store/models/Device';
import { ModalType } from 'src/store/models/Modal';
import { getErrorDetails } from 'src/utils/deviceUtils';

import ListBox from '../ListBox/ListBox';
import classes from './DetectedErrors.module.scss';
import ListError from './ListError/ListError';

export interface DetectedErrorsProps {
  loading?: boolean;
}

const DetectedErrors: FC<DetectedErrorsProps> = ({ loading = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const deviceDetails = useAppSelector((state) => state.device.deviceDetails);
  const errorDetails = useAppSelector((state) => state.product.errors);

  useEffect(() => {
    dispatch(getErrorsTable());
    // eslint-disable-next-line
  }, []);

  const deviceErrors = deviceDetails?.controller?.diagnostic?.variables?.slice(
    DeviceVariables.ERROR_LOG_ID_1,
    DeviceVariables.ERROR_LOG_TIME_15 + 1
  );

  const errors: DeviceError[] = useMemo(
    () =>
      deviceErrors
        ?.map((v, i) => {
          if (v.id % 4 === 0 && deviceErrors[i + 4]) {
            const errorCode = errorDetails?.find(
              (e) => e?.logCode === deviceErrors[i + 1]?.value?.toString()
            );
            return getErrorDetails(
              deviceErrors[i + 1]?.value,
              errorCode?.id,
              deviceErrors[i + 2]?.value
            );
          }
          return null;
        })
        .filter((v) => v)
        .sort((a, b) => b.date - a.date),
    [deviceErrors, errorDetails]
  );

  const bottomLinks = (
    <div className={classes.BottomLinks}>
      {errors?.length > 3 ? (
        <p
          onClick={() =>
            dispatch(
              addModal({
                title: t('ticket_details:detected_errors'),
                data: errors,
                modalType: ModalType.DETECTED_ERRORS,
              })
            )
          }
        >
          {t('ticket_details:error_history')}
        </p>
      ) : (
        <Link to={routes.ERROR_INDEX}>
          {t('ticket_details:view_error_index')}
        </Link>
      )}
      <Link to={routes.PRODUCTS}>{t('ticket_details:common_issues')}</Link>
    </div>
  );

  return (
    <ListBox
      itemsCount={errors?.length}
      loading={loading}
      title={t('ticket_details:detected_errors')}
      bottomNode={bottomLinks}
      emptyMessage={t('ticket_details:no_errors')}
    >
      {errors?.slice(0, 3)?.map((e, i) => (
        <li key={i}>
          <ListError
            error={e}
            onClick={() => navigate(routes.ERROR_INDEX, { state: e })}
          />
        </li>
      ))}
    </ListBox>
  );
};

export default DetectedErrors;
